import styled from 'styled-components';

import { Box } from 'components/Box/Box';
import { Divider } from 'components/Divider/Divider';
import { AddressWithoutLocationForm } from 'components/Form/AddressWithoutLocationForm';
import { CheckboxField } from 'components/Form/CheckboxField';
import { CreditCardFieldWithDefaultAdditionalData } from 'components/Form/CreditCard/CreditCardFieldWithDefaultAdditionalData';
import { CreditCardSecureNotice } from 'components/Form/CreditCard/CreditCardSecureNotice';
import { CurrencyInputField } from 'components/Form/CurrencyInputField';
import { Form } from 'components/Form/Form';
import { FormSubmitFunction } from 'components/Form/FormContext';
import { GeneralError } from 'components/Form/GeneralError';
import { HiddenField } from 'components/Form/HiddenField';
import { InputField } from 'components/Form/InputField';
import { RadioButtonsField } from 'components/Form/Radio/RadioButtonsField';
import { RecaptchaField } from 'components/Form/RecaptchaField';
import { SubmitButton } from 'components/Form/SubmitButton/SubmitButton';
import { Icon } from 'components/Icon/Icon';
import { H3 } from 'components/Text/H3';
import { gutterWidth, transition } from 'theme/theme';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import { cmsGetDonateBlockSubmitButtonText } from './cmsGetDonateBlockSubmitButtonText';

const AMOUNTS = [
  {
    label: '$25',
    value: 25,
  },
  {
    label: '$50',
    value: 50,
  },
  {
    label: '$100',
    value: 100,
  },
  {
    label: getText('Other Amount'),
    value: 'OTHER',
  },
];
const StyledSubmitButton = styled(SubmitButton)`
  svg {
    transition: transform ${transition};
  }

  &:hover,
  &:focus {
    svg {
      transform: translateX(50%);
    }
  }
`;

type DonateBlockFormValues = {
  amount?: string;
  otherAmount?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  country: string | null;
  isForHonoree?: boolean;
};

type Props = {
  onSubmit: FormSubmitFunction<DonateBlockFormValues>;
  campaignId: string | null;
  initialValues: Record<string, unknown>;
};

export function CmsDonateBlockInternalForm({
  onSubmit,
  campaignId,
  initialValues,
}: Props) {
  return (
    <Form
      analyticsTitle="[cms] Donate block internal block"
      usageContext="page"
      data-qa-id="donate-form"
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ getValue }) => {
        const amount = getValue('amount') as string;

        return (
          <>
            <GeneralError />
            <H3>{getText('Your Donation')}</H3>
            <HiddenField
              name="campaignId"
              // @ts-expect-error TS(2322): Type '{ name: string; value: string; }' is not ass... Remove this comment to see the full error message
              value={campaignId}
            />
            <RadioButtonsField
              buttons={AMOUNTS}
              name="amount"
              label={getText('How much would you like to donate? (USD)')}
              onChange={(v) =>
                trackClicked('Preset Donation Amount', {
                  amount: v,
                  campaign_id: campaignId,
                })
              }
              square
            />
            {amount === 'OTHER' ? (
              <CurrencyInputField
                name="otherAmount"
                min={5}
                errorMessage={getText('Please enter an amount of $5 or more')}
              />
            ) : null}

            <InputField
              name="inspiration"
              label={getText(`What inspired your gift to Idealist today?`)}
            />

            <CheckboxField
              name="isForHonoree"
              label={getText(
                'Dedicate this gift in honor or in memory of someone',
              )}
            />
            {getValue('isForHonoree') ? (
              <>
                <H3>{getText('Who is this gift dedicated to?')}</H3>
                <InputField
                  name="honoreeFirstName"
                  label={`${getText(`First Name`)}*`}
                  placeholder={getText('Casey')}
                />
                <InputField
                  name="honoreeLastName"
                  label={`${getText(`Last Name`)}*`}
                  placeholder={getText('Smith')}
                />
                <InputField
                  name="honoreeEmail"
                  type="email"
                  label={getText('Email for E-card (optional)')}
                  description={getText(
                    'An e-card will be sent to the email address provided',
                  )}
                  placeholder={getText('casey.smith@example.com')}
                />

                <Box
                  mb={[gutterWidth / 2, null, gutterWidth]}
                  mt={[`${gutterWidth / 2 - 16}px`, null, gutterWidth - 30]}
                >
                  <Divider dashed marginTop={0} marginBottom={0} />
                </Box>
              </>
            ) : null}

            <Box my={[gutterWidth / 2, null, gutterWidth]}>
              <Divider dashed marginTop={0} marginBottom={0} />
            </Box>

            <H3>{getText('Payment Info')}</H3>

            <div style={{ marginBottom: 22 }}>
              <CreditCardSecureNotice />
            </div>

            <CreditCardFieldWithDefaultAdditionalData wrap />

            <Box my={[gutterWidth / 2, null, gutterWidth]}>
              <Divider dashed marginTop={0} marginBottom={0} />
            </Box>

            <H3>{getText('Billing Info')}</H3>
            <InputField
              name="firstName"
              label={getText('First Name')}
              placeholder={getText('Casey')}
            />
            <InputField
              name="lastName"
              label={getText('Last Name')}
              placeholder={getText('Smith')}
            />
            <AddressWithoutLocationForm
              useUSStateDropdown={getValue('country') === 'US'}
            />

            <Box
              mb={[gutterWidth / 2, null, gutterWidth]}
              mt={[`${gutterWidth / 2 - 16}px`, null, gutterWidth - 30]}
            >
              <Divider dashed marginTop={0} marginBottom={0} />
            </Box>

            <H3>{getText('Contact Info')}</H3>
            <InputField
              type="email"
              name="email"
              placeholder={getText('casey.smith@example.com')}
              label={getText('Email')}
            />
            <InputField
              name="phone"
              placeholder={getText('e.g. 123 555-1234')}
              label={getText('Phone Number (optional)')}
            />

            <Box
              mb={[gutterWidth / 2, null, gutterWidth]}
              mt={[`${gutterWidth / 2 - 16}px`, null, gutterWidth - 30]}
            >
              <Divider dashed marginTop={0} marginBottom={0} />
            </Box>

            <RecaptchaField />

            <Box
              mt={[gutterWidth / 2, null, gutterWidth]}
              display="flex"
              justifyContent="flex-end"
            >
              <StyledSubmitButton size="large">
                {cmsGetDonateBlockSubmitButtonText(
                  amount === 'OTHER'
                    ? (getValue('otherAmount') as string)
                    : amount,
                )}
                <Box display="inline-block" ml="4px">
                  <Icon name="arrow-right" size={18} />
                </Box>
              </StyledSubmitButton>
            </Box>
          </>
        );
      }}
    />
  );
}
